import { SvgIcon, Tooltip } from '@mui/material'
import moment from 'moment'
import React from 'react'
import { formatDaysToDeliveryDisplay } from '../../../hooks/displayTools'

type DeliveryCalendarPopoverProps = {
  production_time: number
  children?: React.ReactNode
}

function CalendarGrid({
  year,
  month,
  today,
  deliveryDate,
}: {
  year: number
  month: number
  today: moment.Moment
  deliveryDate: moment.Moment
}) {
  const firstDay = moment({ year, month, day: 1 })
  const daysInMonth = firstDay.daysInMonth()
  const startOffset = firstDay.day()

  const cells: (number | null)[] = [
    ...Array(startOffset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]
  while (cells.length % 7 !== 0) cells.push(null)

  return (
    <div>
      <div className='mb-2 text-center text-sm font-semibold text-gray-700'>
        {firstDay.format('MMMM YYYY')}
      </div>
      <div className='grid grid-cols-7 gap-y-0.5 text-center text-xs'>
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
          <div key={d} className='pb-1 font-medium text-gray-400'>
            {d}
          </div>
        ))}
        {cells.map((day, idx) => {
          if (!day) return <div key={`e-${idx}`} />
          const date = moment({ year, month, day })
          const isDelivery = date.isSame(deliveryDate, 'day')
          const isToday = date.isSame(today, 'day')

          return (
            <div key={day} className='flex items-center justify-center py-0.5'>
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs ${
                  // eslint-disable-next-line no-nested-ternary
                  isDelivery
                    ? 'font-bold text-pure-white'
                    : isToday
                      ? 'font-semibold text-gray-900 ring-1 ring-gray-400'
                      : 'text-gray-700'
                }`}
                style={
                  isDelivery
                    ? { background: 'linear-gradient(135deg, #1a8f2e, #0d6216)' }
                    : undefined
                }
              >
                {day}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function DeliveryCalendarPopover({ production_time, children }: DeliveryCalendarPopoverProps) {
  const estNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }))
  const today = moment(estNow).startOf('day')

  const deliveryDateStr = formatDaysToDeliveryDisplay(production_time)
  const deliveryDate = deliveryDateStr
    ? (() => {
        const d = moment(deliveryDateStr, 'MMM D').year(today.year())
        if (d.isBefore(today)) d.add(1, 'year')
        return d
      })()
    : today.clone().add(7, 'days')

  return (
    <Tooltip
      title={
        <div className='min-w-55'>
          <CalendarGrid
            year={deliveryDate.year()}
            month={deliveryDate.month()}
            today={today}
            deliveryDate={deliveryDate}
          />
        </div>
      }
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'white',
            color: 'inherit',
            boxShadow: 4,
            borderRadius: 2,
            p: 2,
            maxWidth: 280,
          },
        },
      }}
      arrow={false}
      enterDelay={100}
      leaveDelay={100}
    >
      {children ? (
        <span style={{ cursor: 'pointer' }}>{children}</span>
      ) : (
        <SvgIcon sx={{ fontSize: 16, color: '#555', cursor: 'pointer' }}>
          <path d='M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z' />
        </SvgIcon>
      )}
    </Tooltip>
  )
}
